import type { ColumnDef, Leaves, PriceParams } from '@talos/kyoko';
import { isPricingModeRelative } from '@talos/kyoko';
import { isQuoteRow, type CareOrderBlotterEntity } from '../types';

export const CARE_ORDER_EXPORT_MAKER_COLUMN_PREFIX = 'careOrderExportMaker';
type CareOrderExportMakerSide = 'Bid' | 'Offer';

export function getCareOrderExportMakerColumnId(marketName: string, side: CareOrderExportMakerSide) {
  return `${CARE_ORDER_EXPORT_MAKER_COLUMN_PREFIX}:${marketName}:${side.toLowerCase()}`;
}

export function isCareOrderExportMakerColumnId(colId: string) {
  return colId.startsWith(`${CARE_ORDER_EXPORT_MAKER_COLUMN_PREFIX}:`);
}

function getCareOrderExportMakerPriceField(marketName: string, side: CareOrderExportMakerSide) {
  const marketPriceField = side === 'Bid' ? 'BidPx' : 'OfferPx';
  return `quoteMarketsByName.${marketName}.${marketPriceField}` satisfies Leaves<CareOrderBlotterEntity>;
}

function getCareOrderExportMakerColumn(
  marketName: string,
  marketLabel: string,
  side: CareOrderExportMakerSide
): ColumnDef<CareOrderBlotterEntity> {
  return {
    type: 'price',
    field: getCareOrderExportMakerPriceField(marketName, side),
    id: getCareOrderExportMakerColumnId(marketName, side),
    title: `${marketLabel} - ${side}`,
    hide: true,
    suppressColumnsToolPanel: true,
    params: {
      quoteCurrencyField: 'AmountCurrency',
      isReferencePrice: data => (isQuoteRow(data) ? isPricingModeRelative(data?.PricingMode) : false),
    } satisfies PriceParams<CareOrderBlotterEntity>,
  };
}

export function getCareOrderExportMakerColumns({
  marketNames,
  marketDisplayNameByName,
}: {
  marketNames: string[];
  marketDisplayNameByName: Map<string, string> | undefined;
}): ColumnDef<CareOrderBlotterEntity>[] {
  function getMarketLabel(marketName: string): string {
    return marketDisplayNameByName?.get(marketName) ?? marketName;
  }

  return [
    ...marketNames.map(marketName => getCareOrderExportMakerColumn(marketName, getMarketLabel(marketName), 'Bid')),
    ...marketNames.map(marketName => getCareOrderExportMakerColumn(marketName, getMarketLabel(marketName), 'Offer')),
  ];
}
